// PURPOSE: Shared scheduling types, painting and device-held response credentials.
// CONTRACT: Spec 056; stable slot indices and retry-safe identity before submission.
// DEPENDS: Browser crypto, Intl and optional localStorage.
// CEILING: No session, authentication tokens or respondent email persistence.
export interface Slot { index: number; start: string; end: string }
export interface Person { id: string; name: string; slots: number[]; if_needed?: number[]; internal?: boolean; email?: string | null }
export interface Poll {
  id?: string; token?: string; title: string; status: 'open' | 'closed';
  config: { timezone: string; slot_minutes: number; start_date?: string; end_date?: string };
  slots: Slot[]; people: Person[]; counts: number[]; best_times: (Slot & { count: number })[];
  client_id?: string; response_count?: number; if_needed_counts?: number[];
  inclusive_counts?: number[]; inclusive_best_times?: (Slot & { count: number })[];
}
export function paintSlot(selected: number[], index: number, add: boolean): number[] {
  return add ? [...new Set([...selected, index])].sort((a, b) => a - b) : selected.filter(slot => slot !== index);
}
export const heatLevel = (count: number, total: number) => total ? Math.min(1, Math.max(0, count / total)) : 0;
export interface ResponseIdentity { response_id: string; edit_secret: string; persisted: boolean }
export function storedIdentity(token: string): ResponseIdentity {
  const key = `findtime:${token}`;
  try {
    const saved = JSON.parse(localStorage.getItem(key) || 'null');
    if (saved && typeof saved.response_id === 'string' && /^[0-9a-f-]{36}$/i.test(saved.response_id) && /^[A-Za-z0-9_-]{32,128}$/.test(saved.edit_secret)) return { ...saved, persisted: true };
  } catch { /* Storage may be denied or malformed; editing still works until reload. */ }
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  const identity = { response_id: crypto.randomUUID(), edit_secret: Array.from(bytes, n => n.toString(16).padStart(2, '0')).join(''), persisted: false };
  try { localStorage.setItem(key, JSON.stringify(identity)); identity.persisted = true; } catch { /* Explain the session-only lifetime in the page. */ }
  return identity;
}
export function slotLabel(slot: Slot, timezone: string) {
  const options: Intl.DateTimeFormatOptions = { timeZone: timezone, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
  return `${new Date(slot.start).toLocaleString(undefined, options)} – ${new Date(slot.end).toLocaleTimeString(undefined, { timeZone: timezone, hour: 'numeric', minute: '2-digit' })}`;
}
export async function publicRequest(token: string, init: RequestInit = {}, people?: string[]) {
  const suffix = people === undefined ? '' : `?people=${encodeURIComponent(people.join(','))}`;
  const response = await fetch(`/api/public/findtime/${encodeURIComponent(token)}${suffix}`, { ...init, cache: 'no-store', credentials: 'omit', headers: { 'Content-Type': 'application/json' } });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.error?.message || 'The event could not be loaded.');
  return data as Poll;
}
export async function ownResponse(token: string, identity: ResponseIdentity): Promise<Person | null> {
  const response = await fetch(`/api/public/findtime/${encodeURIComponent(token)}/mine`, {
    method: 'POST', credentials: 'omit', cache: 'no-store', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ response_id: identity.response_id, edit_secret: identity.edit_secret }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.error?.message || 'Your saved response could not be loaded.');
  return data.response || null;
}
